import { createFileRoute, useRouter } from '@tanstack/react-router';
import { RoutePermissionDenied } from '@/components/molecules';
import { Skeleton } from '@/components/atoms/skeleton';
import { MEMBERSHIP_DETAIL_SEARCH } from '@/lib/memberships/membership-route-search';
import { MembershipEditPanel } from '@/components/organisms/memberships/MembershipEditPanel';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useRoutePermission } from '@/hooks/navigation/useRoutePermission';
import { apiGet, operationsClient } from '@/lib/api';
import { membershipEndpoints } from '@/lib/api/endpoints';
import { membershipKeys } from '@/lib/queryKeys';
import type { Membership } from '@/types/membership';

export const Route = createFileRoute('/_dashboard/dashboard/memberships/$id/edit')({
  component: EditMembershipPage,
});

function EditMembershipPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const canEditMemberships = useRoutePermission('memberships:edit');

  const { data, isLoading } = useApiQuery<{ data: Membership }>({
    queryKey: membershipKeys.detail(id),
    queryFn: () => apiGet<{ data: Membership }>(operationsClient, membershipEndpoints.detail(id)),
    enabled: canEditMemberships,
  });

  const membership = data?.data;

  function closeToDetail(membershipId = id, discardRetentionOffer = false) {
    if (discardRetentionOffer) {
      window.sessionStorage.removeItem(`membership-retention-offer:${id}`);
    }
    void router.navigate({
      to: '/dashboard/memberships/$id',
      params: { id: membershipId },
      search: MEMBERSHIP_DETAIL_SEARCH,
    });
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (!canEditMemberships) {
    return <RoutePermissionDenied message="You do not have permission to edit memberships." />;
  }

  if (!membership) {
    return <div className="p-4 text-sm text-muted-foreground">Membership not found.</div>;
  }

  return (
    <MembershipEditPanel
      membership={membership}
      onCancel={() => closeToDetail(id, true)}
      onUpdated={(updated) => closeToDetail(updated.id)}
    />
  );
}
